import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  Typography,
  Divider
} from '@mui/material';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%'
  },
  content: {
    padding: theme.spacing(2)
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1)
  }
}));

const ActivityReport = props => {
  const { staffActivities } = props;
  const classes = useStyles();

  // Group activities by type
  const groupByType = () => {
    const grouped = {};
    
    staffActivities.forEach(activity => {
      const type = activity.operationType;
      
      if (!grouped[type]) {
        grouped[type] = [];
      }
      
      grouped[type].push(activity);
    });
    
    return grouped;
  };

  // Group activities by staff
  const groupByStaff = () => {
    const grouped = {};
    
    staffActivities.forEach(activity => {
      const staff = activity.staffName;
      
      if (!grouped[staff]) {
        grouped[staff] = [];
      }
      
      grouped[staff].push(activity);
    });
    
    return grouped;
  };

  // Count activities and total amount by type
  const getTypeStats = (activities) => {
    const count = activities.length;
    const totalAmount = activities.reduce((sum, activity) => sum + activity.price, 0);
    return { count, totalAmount };
  };

  const typeGroups = groupByType();
  const staffGroups = groupByStaff();

  return (
    <Card className={classes.root}>
      <CardContent className={classes.content}>
        <Typography variant="h5">Activity Summary Report</Typography>
        
        <Typography variant="h6" className={classes.sectionTitle}>
          By Activity Type
        </Typography>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Activity Type</TableCell>
                <TableCell>Count</TableCell>
                <TableCell>Total Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(typeGroups).map(([type, activities]) => {
                const { count, totalAmount } = getTypeStats(activities);
                return (
                  <TableRow key={type}>
                    <TableCell>{type}</TableCell>
                    <TableCell>{count}</TableCell>
                    <TableCell>${totalAmount.toFixed(2)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h6" className={classes.sectionTitle}>
          By Staff Member
        </Typography>
        {Object.entries(staffGroups).map(([staff, activities]) => (
          <div key={staff}>
            <Typography variant="subtitle1">{staff}</Typography>
            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Activity Type</TableCell>
                    <TableCell>Count</TableCell>
                    <TableCell>Total Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(
                    activities.reduce((acc, activity) => {
                      const { operationType } = activity;
                      if (!acc[operationType]) {
                        acc[operationType] = { 
                          count: 0, 
                          totalAmount: 0 
                        };
                      }
                      acc[operationType].count += 1;
                      acc[operationType].totalAmount += activity.price;
                      return acc;
                    }, {})
                  ).map(([type, { count, totalAmount }]) => (
                    <TableRow key={`${staff}-${type}`}>
                      <TableCell>{type}</TableCell>
                      <TableCell>{count}</TableCell>
                      <TableCell>${totalAmount.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

ActivityReport.propTypes = {
  staffActivities: PropTypes.array.isRequired
};

export default ActivityReport; 